import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { API_URL } from '../Config';
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  User, 
  FileText, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Edit,
  Trash2,
  ChevronRight,
  Mail
} from 'lucide-react';

const DetailConge = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [conge, setConge] = useState(null);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchCongeDetails();
  }, [id]);

  const fetchCongeDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/conges/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setConge(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des détails');
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      'cree': { 
        label: 'En attente', 
        color: 'bg-yellow-50 text-yellow-700 border-yellow-200',
        icon: <AlertCircle className="w-4 h-4" />
      },
      'valide_par_validateur': { 
        label: 'Validé', 
        color: 'bg-blue-50 text-blue-700 border-blue-200',
        icon: <CheckCircle className="w-4 h-4" />
      },
      'refuse_par_validateur': { 
        label: 'Refusé', 
        color: 'bg-red-50 text-red-700 border-red-200',
        icon: <XCircle className="w-4 h-4" />
      },
      'confirme_par_chef': { 
        label: 'Confirmé', 
        color: 'bg-green-50 text-green-700 border-green-200',
        icon: <CheckCircle className="w-4 h-4" />
      },
      'refuse_par_chef': { 
        label: 'Refusé', 
        color: 'bg-red-50 text-red-700 border-red-200',
        icon: <XCircle className="w-4 h-4" />
      },
    };
    return configs[status] || { 
      label: status, 
      color: ' text-gray-700 border-gray-200',
      icon: <AlertCircle className="w-4 h-4" />
    };
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd MMMM yyyy', { locale: fr });
    } catch (error) {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    try {
      return format(new Date(dateString), "dd/MM/yyyy à HH:mm", { locale: fr });
    } catch (error) {
      return dateString;
    }
  };

  const canEdit = () => {
    return conge?.status === 'cree' && conge?.user_id === user.id;
  };

  const handleDelete = async () => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette demande ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/conges/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      toast.success('Demande supprimée avec succès');
      navigate('/mes-conges');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
      console.error('Erreur:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-3 text-gray-500 text-sm">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!conge) {
    return (
      <div className="min-h-screen  flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Demande non trouvée</h3>
          <p className="text-gray-500 text-sm mb-6">
            La demande de congé que vous recherchez n'existe pas ou a été supprimée.
          </p>
          <button
            onClick={() => navigate('/mes-conges')}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            Retour aux congés
          </button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(conge.status);

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/mes-conges')}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux congés
          </button>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Détail de la demande</h1>
            </div>
            
            <div className="flex items-center gap-3">
              <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm border ${statusConfig.color}`}>
                {statusConfig.icon}
                <span className="ml-1.5">{statusConfig.label}</span>
              </div>
              
              {canEdit() && (
                <div className="flex gap-2">
                  <Link
                    to={`/conges/modifier/${conge.id}`}
                    className="p-2 text-gray-600 hover:text-gray-900"
                  >
                    <Edit className="w-4 h-4" />
                  </Link>
                  <button
                    onClick={handleDelete}
                    className="p-2 text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Informations générales */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Informations</h2>
              </div>
              
              <div className="p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-2">Demandeur</label>
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-gray-900">{conge.user?.nom || '-'}</span>
                      </div>
                      {conge.user?.email && (
                        <div className="flex items-center mt-1">
                          <Mail className="w-3 h-3 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-500">{conge.user.email}</span>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm text-gray-600 mb-2">Type de congé</label>
                      <div className={`inline-flex px-3 py-1 text-sm rounded-full ${
                        conge.type === 'exceptionnel' 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {conge.type === 'exceptionnel' ? 'Exceptionnel' : 'Normal'}
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-2">Date de demande</label>
                      <div className="text-gray-900 text-sm">{formatDateTime(conge.created_at)}</div>
                    </div>
                    
                    <div>
                      <label className="block text-sm text-gray-600 mb-2">Durée</label>
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-gray-900">{conge.duree} jour{conge.duree > 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Période */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <label className="block text-sm text-gray-600 mb-3">Période</label>
                  <div className="flex items-center text-gray-900">
                    <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                    <span>Du {formatDate(conge.date_debut)} au {formatDate(conge.date_fin)}</span>
                  </div>
                </div>

                {/* Motif */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <label className="block text-sm text-gray-600 mb-3">Motif</label>
                  <div className=" border border-gray-200 rounded p-4">
                    <p className="text-gray-700 whitespace-pre-line">{conge.motif}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Détails exceptionnels */}
            {conge.exception_details && conge.exception_details.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">Jours exceptionnels</h2>
                </div>
                
                <div className="p-6">
                  <div className="space-y-4">
                    {conge.exception_details.map((detail, index) => (
                      <div key={index} className="border-l-2 border-purple-500 pl-4 py-3">
                        <div className="text-sm text-gray-900 font-medium">
                          {format(new Date(detail.jour), 'dd MMMM yyyy', { locale: fr })}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">{detail.motif_jour}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Historique */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Historique</h2>
              </div>
              
              <div className="p-6">
                {conge.validations && conge.validations.length > 0 ? (
                  <div className="space-y-4">
                    {conge.validations.map((validation, index) => (
                      <div key={index} className="border-l-2 border-gray-200 pl-4 py-3">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {validation.user?.name}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {validation.role === 'validateur' ? 'Validateur' : 
                               validation.role === 'chef_equipe' ? 'Chef d\'équipe' : validation.role}
                            </div>
                          </div>
                          
                          <div className={`mt-2 sm:mt-0 inline-flex items-center px-2.5 py-0.5 text-xs rounded-full ${
                            validation.action === 'validation' || validation.action === 'confirmation'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {validation.action === 'validation' ? 'Validé' : 
                             validation.action === 'confirmation' ? 'Confirmé' : 'Refusé'}
                          </div>
                        </div>
                        
                        {validation.justification && (
                          <div className="mt-3">
                            <div className="text-xs text-gray-600 mb-1">Justification</div>
                            <div className="text-sm text-gray-700  p-3 rounded border">
                              {validation.justification}
                            </div>
                          </div>
                        )}
                        
                        <div className="text-xs text-gray-500 mt-3">
                          {formatDateTime(validation.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <AlertCircle className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">Aucune validation effectuée</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Progression */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Progression</h2>
              </div>
              
              <div className="p-6">
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${
                      conge.status !== 'cree' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700'
                    }`}>
                      {conge.status !== 'cree' ? '✓' : '1'}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">Créé</div>
                      <div className="text-xs text-gray-500">Demande soumise</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${
                      ['valide_par_validateur', 'confirme_par_chef', 'refuse_par_validateur'].includes(conge.status)
                        ? (conge.status === 'refuse_par_validateur' ? 'bg-red-500 text-white' : 'bg-green-500 text-white')
                        : 'bg-gray-200 text-gray-700'
                    }`}>
                      {['valide_par_validateur', 'confirme_par_chef', 'refuse_par_validateur'].includes(conge.status)
                        ? (conge.status === 'refuse_par_validateur' ? '✗' : '✓') : '2'}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">Validateur</div>
                      <div className="text-xs text-gray-500">Validation RH</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${
                      ['confirme_par_chef', 'refuse_par_chef'].includes(conge.status)
                        ? (conge.status === 'refuse_par_chef' ? 'bg-red-500 text-white' : 'bg-green-500 text-white')
                        : 'bg-gray-200 text-gray-700'
                    }`}>
                      {['confirme_par_chef', 'refuse_par_chef'].includes(conge.status)
                        ? (conge.status === 'refuse_par_chef' ? '✗' : '✓') : '3'}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">Chef d'équipe</div>
                      <div className="text-xs text-gray-500">Confirmation finale</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Infos rapides */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Résumé</h2>
              </div>
              
              <div className="p-6">
                <div className="space-y-3">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Statut</div>
                    <div className="text-sm text-gray-900">{statusConfig.label}</div>
                  </div>
                  
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Type</div>
                    <div className="text-sm text-gray-900 capitalize">{conge.type}</div>
                  </div>
                  
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Durée</div>
                    <div className="text-sm text-gray-900">{conge.duree} jour{conge.duree > 1 ? 's' : ''}</div>
                  </div>
                  
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Mise à jour</div>
                    <div className="text-sm text-gray-900">{formatDateTime(conge.updated_at || conge.created_at)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailConge;